import { Stack } from 'common/ui/Layout';
import { RightDrawerSection } from './RightDrawerSection';
import Text from 'common/ui/Text';
import { Colors } from 'styles';
import type { RightDrawerPreviewItemType, RightDrawerTabType } from './types';
import { Ellipsis } from 'common/ui/Ellipsis';
import { RightDrawerActionIcon } from './RightDrawerActionIcon';
import { RightDrawerAction } from './types';
import { useNavigate } from 'react-router-dom';
import { Renderer } from '../../RichTextEditor/renderer/Renderer';
import { memo, useState } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { RightDrawerHeaderIcon } from './RightDrawerHeaderIcon';
import { useCaseStudyParamsSuffix } from 'hooks/studyParams';
import CircularProgress from '@mui/material/CircularProgress';
import { css } from 'styled-components';
import { useCompactUI } from 'hooks/useCompactUI';
import { getRightDrawerClonePath, getRightDrawerEditPath } from './paths';
import { RightDrawerPreviewBox } from './RightDrawerPreviewBox';
import { RightDrawerEmptyPreviewState } from './RightDrawerEmptyPreviewState';

type Props = {
  previewItem: Nullable<RightDrawerPreviewItemType>;
  activeTab: RightDrawerTabType;
  isPreviewLoading: boolean;
  basePath: string;
};

export const RightDrawerPreviewSection = memo(
  ({ previewItem, activeTab, isPreviewLoading, basePath }: Props): React.ReactElement => {
    const { showCompactHeight } = useCompactUI();
    const navigate = useNavigate();
    const priorsSuffix = useCaseStudyParamsSuffix();
    const [expanded, setExpanded] = useState(true);

    return (
      <RightDrawerSection
        css={css`
          padding: ${showCompactHeight ? '8px' : '8px 24px'};
        `}
      >
        <Stack alignX="between" alignY="center">
          <Text
            color={Colors.gray10}
            variant="body2"
            css={{
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              flexGrow: 1,
            }}
          >
            {previewItem == null ? (
              'Preview'
            ) : (
              <Stack alignY="center" css={{ minWidth: 0 }}>
                <Ellipsis>{`Preview: ${previewItem.name}`}</Ellipsis>
              </Stack>
            )}
          </Text>
          <Stack alignY="center">
            {previewItem?.action != null && (
              <RightDrawerActionIcon
                type={previewItem.action}
                onClick={() => {
                  if (previewItem == null) {
                    return;
                  }

                  if (previewItem.action === RightDrawerAction.Edit) {
                    navigate(
                      getRightDrawerEditPath({
                        basePath,
                        activeTab,
                        itemId: previewItem.id,
                        priorsSuffix,
                      })
                    );
                  } else {
                    navigate(
                      getRightDrawerClonePath({
                        basePath,
                        activeTab,
                        itemId: previewItem.id,
                      })
                    );
                  }
                }}
              />
            )}
            <RightDrawerHeaderIcon
              css={{ marginLeft: 'auto', cursor: 'pointer' }}
              as={expanded ? ExpandLessIcon : ExpandMoreIcon}
              onClick={() => setExpanded(!expanded)}
              onFocus={() => setExpanded(!expanded)}
            />
          </Stack>
        </Stack>
        {expanded && (
          <RightDrawerPreviewBox
            vertical
            data-stack-stretch
            data-testid="right-drawer-preview"
            css={{ height: '100%' }}
          >
            {previewItem != null && !isPreviewLoading ? (
              <Stack vertical space="medium" css={{ width: '100%', padding: '4px 12px' }}>
                {Array.isArray(previewItem.content) && [
                  <Renderer key="right-drawer-renderer" content={previewItem.content} />,
                ]}
              </Stack>
            ) : isPreviewLoading ? (
              <Stack alignX="center" alignY="center" data-stack-stretch>
                <CircularProgress size="4rem" />
              </Stack>
            ) : (
              <RightDrawerEmptyPreviewState>No item selected.</RightDrawerEmptyPreviewState>
            )}
          </RightDrawerPreviewBox>
        )}
      </RightDrawerSection>
    );
  }
);

RightDrawerPreviewSection.displayName = 'RightDrawerPreviewSection';
